import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface UIState {
  selectedBudget: { type: 'real' } | { type: 'scenario'; id: string } | { type: 'snapshot'; id: string };
  personFilter: 'couple' | 'p1' | 'p2';
  monthYear: 'month' | 'year';
  viewMode: 'simplified' | 'stats';
  editingEntity: 'budget' | 'patrimoine' | null;
  collapseState: Record<string, boolean>;
  /** Phase B PLAN-EDITION-MOBILE : élément sélectionné en mode vue (mobile).
   *  Transitoire — jamais persisté. */
  viewSelection: { type: 'line' | 'category' | 'domain'; id: string } | null;
  /** Échelle de temps des courbes d'évolution solde/épargne (partagée entre
   *  les deux cartes) : mois = 30 derniers jours (1 pt/jour), année = 12
   *  derniers mois (1 pt/mois), tout = historique (1 pt/mois). */
  evoScale: 'month' | 'year' | 'all';
  /** Échelle de la courbe patrimoine (mensuelle par nature) : month = 12
   *  derniers mois, all = historique complet. */
  evoScalePatri: 'month' | 'all';

  setSelectedBudget: (budget: UIState['selectedBudget']) => void;
  setPersonFilter: (filter: UIState['personFilter']) => void;
  toggleMonthYear: () => void;
  toggleViewMode: () => void;
  setEditingEntity: (entity: UIState['editingEntity']) => void;
  setCollapseState: (key: string, open: boolean) => void;
  setViewSelection: (sel: UIState['viewSelection']) => void;
  setEvoScale: (scale: UIState['evoScale']) => void;
  setEvoScalePatri: (scale: UIState['evoScalePatri']) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      selectedBudget: { type: 'real' },
      personFilter: 'couple',
      monthYear: 'month',
      viewMode: 'simplified',
      editingEntity: null,
      collapseState: {},
      viewSelection: null,
      evoScale: 'year',
      evoScalePatri: 'month',

      setSelectedBudget: (budget) => set({ selectedBudget: budget }),
      setPersonFilter: (filter) => set({ personFilter: filter }),
      toggleMonthYear: () => set((s) => ({ monthYear: s.monthYear === 'month' ? 'year' : 'month' })),
      toggleViewMode: () => set((s) => ({ viewMode: s.viewMode === 'simplified' ? 'stats' : 'simplified' })),
      setEditingEntity: (entity) => set({ editingEntity: entity }),
      setCollapseState: (key, open) => set((s) => ({
        collapseState: { ...s.collapseState, [key]: open },
      })),
      setViewSelection: (sel) => set({ viewSelection: sel }),
      setEvoScale: (scale) => set({ evoScale: scale }),
      setEvoScalePatri: (scale) => set({ evoScalePatri: scale }),
    }),
    {
      name: 'cashpulse-ui',
      // D1 (2.10) : seules les préférences d'affichage sont persistées.
      // PAS selectedBudget (un scénario/snapshot supprimé ne doit pas être
      // re-sélectionné au boot) ni editingEntity (état transitoire d'édition).
      partialize: (s) => ({
        collapseState: s.collapseState,
        personFilter: s.personFilter,
        monthYear: s.monthYear,
        viewMode: s.viewMode,
        evoScale: s.evoScale,
        evoScalePatri: s.evoScalePatri,
      }),
    },
  ),
);
